import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  DEFAULT_DAYS,
  getActiveDays,
  getAvailableDays,
  getWeekFromMap,
  isDefaultDay,
  type DayName,
  DAY_LABELS,
  DAY_ORDER,
} from "../lib/schedule"
import { authMiddleware } from "../middleware/auth"
import { callAdminFn } from "../utils/admin"
import { routesGet } from "~/utils/routes"
import { scheduleGet } from "~/utils/schedule"
import { weekSave } from "~/utils/weeks"

export const Route = createFileRoute("/admin")({
  server: {
    middleware: [authMiddleware],
  },
  loader: async () => {
    const [schedule, routes] = await Promise.all([scheduleGet(), routesGet()])
    return { ...schedule, routes }
  },
  component: AdminComponent,
})

interface DayEditorState {
  title: string
  url: string
}

function AdminComponent() {
  const router = useRouter()
  const { weekKeys, weeksRecord, routes, currentWeekKey } = Route.useLoaderData()
  const weeks = useMemo(() => new Map(Object.entries(weeksRecord)), [weeksRecord])

  const defaultWeek =
    weekKeys.includes(currentWeekKey)
      ? currentWeekKey
      : weekKeys[Math.floor(weekKeys.length / 2)] ?? weekKeys[0]

  const [selectedWeek, setSelectedWeek] = useState(defaultWeek)
  const [activeDays, setActiveDays] = useState<DayName[]>(() =>
    getActiveDays(getWeekFromMap(weeks, defaultWeek)),
  )
  const [editors, setEditors] = useState<Partial<Record<DayName, DayEditorState>>>({})
  const [toast, setToast] = useState<string | null>(null)

  const loadWeek = useCallback(
    (key: string) => {
      const week = getWeekFromMap(weeks, key)
      const days = getActiveDays(week)
      setActiveDays(days)
      const next: Partial<Record<DayName, DayEditorState>> = {}
      for (const day of days) {
        const route = week.days[day]
        next[day] = {
          title: route?.title ?? "",
          url: route?.url ?? "",
        }
      }
      setEditors(next)
    },
    [weeks],
  )

  useEffect(() => {
    loadWeek(selectedWeek)
  }, [selectedWeek, loadWeek])

  const availableDays = getAvailableDays(activeDays)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2800)
  }

  function updateEditor(day: DayName, patch: Partial<DayEditorState>) {
    setEditors((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }))
  }

  function addDay(day: DayName) {
    if (activeDays.includes(day)) return
    const sorted = [...activeDays, day].sort(
      (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b),
    )
    setActiveDays(sorted)
    setEditors((prev) => ({
      ...prev,
      [day]: prev[day] ?? { title: "", url: "" },
    }))
  }

  function removeDay(day: DayName) {
    setActiveDays((prev) => prev.filter((d) => d !== day))
  }

  function buildDaysPayload() {
    const days: Partial<
      Record<DayName, { routeName: string; routeUrl: string }>
    > = {}
    for (const day of activeDays) {
      const ed = editors[day]
      if (!ed) continue
      const routeName = ed.title.trim()
      const routeUrl = ed.url.trim()
      if (!routeName && !routeUrl) continue
      if (!routeName || !routeUrl) continue
      days[day] = { routeName, routeUrl }
    }
    return days
  }

  async function saveWeek() {
    try {
      await callAdminFn(weekSave, {
        weekKey: selectedWeek,
        activeDays,
        days: buildDaysPayload(),
      })
      await router.invalidate()
      showToast("Week saved")
    } catch {
      showToast("Save failed")
    }
  }

  async function clearWeek() {
    try {
      await callAdminFn(weekSave, {
        weekKey: selectedWeek,
        activeDays: [...DEFAULT_DAYS],
        days: {},
      })
      await router.invalidate()
      showToast("Week cleared")
    } catch {
      showToast("Clear failed")
    }
  }

  function applyLibraryRoute(day: DayName, routeId: string) {
    const lib = routes.find((r) => r.id === routeId)
    if (!lib) return
    updateEditor(day, { title: lib.name, url: lib.url })
  }

  return (
    <>
      <div className="hidden min-h-[70vh] items-center justify-center bg-brew-surface-warm p-brew-12 px-brew-6 text-center max-[920px]:flex">
        <div className="max-w-[420px] rounded-brew-md shadow-[0_0_0_1px_var(--color-brew-border)] bg-brew-bg p-brew-8">
          <p className="mb-brew-3 font-mono text-brew-xs uppercase tracking-widest text-brew-muted">
            Desktop only
          </p>
          <h2 className="mb-brew-3 font-display text-brew-2xl uppercase leading-tight tracking-[var(--tracking-brew-display)]">
            Open admin on a larger screen
          </h2>
          <p className="mx-auto mb-5 max-w-prose font-medium text-brew-muted">
            Route editing is built for desktop web. View the public schedule on your
            phone instead.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-brew-pill border border-brew-accent bg-brew-accent px-6 py-3 text-brew-base font-medium text-brew-accent-on no-underline hover:bg-brew-accent-hover"
          >
            Back to schedule
          </Link>
        </div>
      </div>

      <div className="max-[920px]:hidden">
        <header className="sticky top-0 z-10 border-b border-brew-border-soft bg-brew-bg">
          <div className="mx-auto max-w-[var(--container-brew-max)] px-brew-gutter-desktop max-[960px]:px-brew-gutter-tablet max-[640px]:px-brew-gutter-phone">
            <div className="flex min-h-[60px] items-center justify-between">
              <Link
                to="/"
                className="font-logo text-brew-base font-normal tracking-widest text-brew-fg no-underline hover:text-brew-muted"
              >
                Brew Rides
              </Link>
              <nav className="flex gap-brew-8">
                <Link to="/" className="text-brew-base font-medium text-brew-fg no-underline hover:text-brew-muted">
                  Public schedule
                </Link>
                <Link
                  to="/admin"
                  aria-current="page"
                  className="text-brew-base font-medium text-brew-fg underline underline-offset-4"
                >
                  Admin
                </Link>
              </nav>
            </div>
          </div>
        </header>

        <main>
          <section className="mx-auto max-w-[var(--container-brew-max)] px-brew-gutter-desktop py-brew-section-tablet max-[960px]:px-brew-gutter-tablet max-[640px]:px-brew-gutter-phone">
            <p className="mb-brew-3 font-mono text-brew-xs uppercase tracking-widest text-brew-muted">
              Club operations
            </p>
            <h1 className="mb-brew-2 font-display text-[clamp(var(--font-size-brew-2xl),4vw,var(--font-size-brew-3xl))] font-medium uppercase leading-[var(--leading-brew-tight)] tracking-[var(--tracking-brew-display)]">
              Update weekly routes
            </h1>
            <p className="max-w-[52ch] font-medium text-brew-muted">
              Pick a week and edit rides. Tuesday and Thursday are always available;
              add other days when needed.
            </p>
          </section>

          <section className="mx-auto grid max-w-[var(--container-brew-max)] grid-cols-[280px_1fr] items-start gap-brew-8 px-brew-gutter-desktop pb-brew-section-desktop max-[960px]:px-brew-gutter-tablet max-[640px]:px-brew-gutter-phone">
            <aside className="rounded-brew-md bg-brew-bg p-brew-8 shadow-[0_0_0_1px_var(--color-brew-border)]">
              <label htmlFor="week-select" className="mb-brew-2 block text-brew-sm font-medium text-brew-muted">
                Week
              </label>
              <select
                id="week-select"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="w-full rounded-brew-lg border border-brew-border bg-brew-surface px-4 py-3 text-brew-base font-medium text-brew-fg"
              >
                {weekKeys.map((key) => (
                  <option key={key} value={key}>
                    {getWeekFromMap(weeks, key).label}
                  </option>
                ))}
              </select>
              <p className="mt-brew-4 text-brew-sm font-medium text-brew-muted">
                Week starts Monday. Default ride days are Tuesday and Thursday.
              </p>
            </aside>

            <div>
              {activeDays.map((day) => {
                const ed = editors[day] ?? { title: "", url: "" }
                return (
                  <div
                    key={day}
                    className="mb-brew-6 rounded-brew-md bg-brew-bg p-brew-8 shadow-[0_0_0_1px_var(--color-brew-border)]"
                  >
                    <div className="mb-brew-4 flex items-center justify-between gap-brew-3">
                      <h2 className="font-body text-brew-xl font-medium">
                        {DAY_LABELS[day]}
                        {isDefaultDay(day) && (
                          <span className="ml-brew-3 font-mono text-brew-xs uppercase tracking-wide text-brew-muted">
                            Default
                          </span>
                        )}
                      </h2>
                      {!isDefaultDay(day) && (
                        <button
                          type="button"
                          className="rounded-brew-pill px-3 py-2 text-brew-sm text-brew-muted hover:text-brew-danger"
                          onClick={() => removeDay(day)}
                        >
                          Remove day
                        </button>
                      )}
                    </div>

                    <div className="mb-brew-4">
                      <label
                        htmlFor={`${day}-library`}
                        className="mb-brew-2 block text-brew-sm font-medium text-brew-muted"
                      >
                        Route library (optional)
                      </label>
                      <select
                        id={`${day}-library`}
                        className="w-full rounded-brew-lg border border-brew-border bg-brew-surface px-4 py-3 text-brew-base font-medium"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) applyLibraryRoute(day, e.target.value)
                        }}
                      >
                        <option value="">Choose a saved route…</option>
                        {routes.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-brew-4 flex flex-col gap-brew-2">
                      <label
                        htmlFor={`${day}-title`}
                        className="text-brew-sm font-medium text-brew-muted"
                      >
                        Route title
                      </label>
                      <input
                        id={`${day}-title`}
                        className="w-full rounded-brew-sm border border-brew-border bg-brew-surface px-4 py-3 text-brew-base font-medium"
                        placeholder="e.g. River Road Loop"
                        value={ed.title}
                        onChange={(e) => updateEditor(day, { title: e.target.value })}
                      />
                    </div>

                    <div className="flex flex-col gap-brew-2">
                      <label
                        htmlFor={`${day}-url`}
                        className="text-brew-sm font-medium text-brew-muted"
                      >
                        Source URL
                      </label>
                      <input
                        id={`${day}-url`}
                        type="url"
                        className="w-full rounded-brew-sm border border-brew-border bg-brew-surface px-4 py-3 text-brew-base font-medium"
                        placeholder="https://ridewithgps.com/..."
                        value={ed.url}
                        onChange={(e) => updateEditor(day, { url: e.target.value })}
                      />
                    </div>
                  </div>
                )
              })}

              {availableDays.length > 0 && (
                <div className="mt-brew-6 flex flex-wrap items-center gap-brew-3 border-t border-brew-border-soft pt-brew-6">
                  <label htmlFor="add-day-select" className="text-brew-sm font-medium text-brew-muted">
                    Add ride day
                  </label>
                  <select
                    id="add-day-select"
                    className="min-w-[180px] rounded-brew-lg border border-brew-border bg-brew-surface px-4 py-3"
                    defaultValue={availableDays[0]}
                  >
                    {availableDays.map((day) => (
                      <option key={day} value={day}>
                        {DAY_LABELS[day]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-brew-pill border-[1.5px] border-brew-border bg-transparent px-6 py-3 text-brew-base font-medium hover:border-brew-muted"
                    onClick={() => {
                      const sel = document.getElementById(
                        "add-day-select",
                      ) as HTMLSelectElement
                      addDay(sel.value as DayName)
                    }}
                  >
                    Add day
                  </button>
                </div>
              )}

              <div className="mt-brew-6 flex flex-wrap items-center gap-brew-3">
                <button
                  type="button"
                  className="inline-flex items-center rounded-brew-pill border border-brew-accent bg-brew-accent px-6 py-3 text-brew-base font-medium text-brew-accent-on hover:bg-brew-accent-hover"
                  onClick={() => void saveWeek()}
                >
                  Save week routes
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-brew-pill border-[1.5px] border-brew-border bg-transparent px-6 py-3 text-brew-base font-medium hover:border-brew-muted"
                  onClick={() => void clearWeek()}
                >
                  Clear all routes
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={`fixed bottom-brew-6 right-brew-6 rounded-brew-pill bg-brew-accent px-brew-4 py-brew-3 text-brew-sm font-medium text-brew-accent-on transition-all duration-brew-base ${
          toast ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
        }`}
      >
        {toast}
      </div>
    </>
  )
}
